/**
 * CPU Page JavaScript
 * Handles fetching, displaying, and comparing CPU data
 */

let cpuDatabase = [];

document.addEventListener('DOMContentLoaded', async function() {
    // Only run on CPU page
    if (!document.querySelector('.cpu-page')) {
        return;
    }

    // Fetch CPU data
    try {
        const response = await fetch('/assets/data/cpus.json');
        if (!response.ok) {
            throw new Error('Failed to load CPU database');
        }
        cpuDatabase = await response.json();
    } catch (error) {
        console.error('Error loading CPU data:', error);
        const container = document.querySelector('.cpu-list-container');
        if (container) {
            container.innerHTML = '<p>Error loading CPU data. Please try again later.</p>';
        }
        return;
    }

    // Get CPU model from URL if available
    const urlParams = new URLSearchParams(window.location.search);
    const cpuModel = urlParams.get('model');
    
    if (cpuModel) {
        // If CPU model is specified in URL, fetch and display its data
        fetchCpuData(cpuModel);
    } else {
        // Otherwise, load the list of available CPUs
        loadCpuList();
    }
    
    // Handle CPU search form submission
    const searchForm = document.getElementById('cpu-search-form');
    if (searchForm) {
        searchForm.addEventListener('submit', function(e) {
            e.preventDefault();
            const searchTerm = document.getElementById('cpu-search').value.trim();
            if (searchTerm) {
                window.location.href = `/cpu?model=${encodeURIComponent(searchTerm)}`;
            }
        });
    }
    
    // Handle CPU comparison
    const compareButton = document.getElementById('compare-button');
    if (compareButton) {
        compareButton.addEventListener('click', function() {
            const selectedCpu = document.getElementById('compare-cpu').value;
            if (selectedCpu) {
                compareCpus(cpuModel, selectedCpu);
            }
        });
    }
});

/**
 * Fetch CPU data by model name or ID
 * @param {string} cpuModel - The CPU model to fetch
 */
function fetchCpuData(cpuModel) {
    // Search our loaded database
    const cpuData = cpuDatabase.find(cpu => 
        cpu.id.toLowerCase() === cpuModel.toLowerCase() || 
        cpu.model.toLowerCase() === cpuModel.toLowerCase()
    );
    
    if (cpuData) {
        // Update the UI with the CPU data
        updateCpuDetails(cpuData);
        
        // Load comparison options
        loadComparisonOptions(cpuData.id);
    } else {
        // Handle case where CPU is not found
        showCpuNotFound(cpuModel);
    }
}

/**
 * Update the UI with CPU details
 * @param {Object} cpuData - The CPU data object
 */
function updateCpuDetails(cpuData) {
    // Update CPU details in the UI
    document.querySelector('.cpu-model-title').textContent = cpuData.model;
    document.getElementById('cpu-architecture').textContent = cpuData.architecture;
    document.getElementById('cpu-cores').textContent = cpuData.cores;
    document.getElementById('cpu-threads').textContent = cpuData.threads;
    document.getElementById('cpu-base-clock').textContent = `${cpuData.baseClockGHz} GHz`;
    document.getElementById('cpu-boost-clock').textContent = `${cpuData.boostClockGHz} GHz`;
    document.getElementById('cpu-tdp').textContent = `${cpuData.tdpWatts} W`;
    document.getElementById('cpu-release-date').textContent = cpuData.releaseDate;
    
    // Update benchmark scores
    document.getElementById('single-thread-score').textContent = cpuData.benchmarks.singleThread;
    document.getElementById('multi-thread-score').textContent = cpuData.benchmarks.multiThread;
    
    // Create benchmark chart
    createBenchmarkChart(cpuData);
}

/**
 * Show message when CPU is not found
 * @param {string} cpuModel - The CPU model that was searched for
 */
function showCpuNotFound(cpuModel) {
    const container = document.querySelector('.cpu-details-container');
    if (container) {
        container.innerHTML = `
            <div class="cpu-not-found">
                <h3>CPU Not Found</h3>
                <p>Sorry, we couldn't find information for "${cpuModel}".</p>
                <p>Please try another CPU model or browse our available CPUs below.</p>
                <a href="/cpu" class="cpu-details-link">Browse All CPUs</a>
            </div>
            <div class="cpu-list-container">
                <h3>Popular CPU Models</h3>
                <div class="cpu-grid" id="popular-cpus"></div>
            </div>
        `;
        
        // Load some popular CPUs
        const popularCpusContainer = document.getElementById('popular-cpus');
        if (popularCpusContainer) {
            cpuDatabase.slice(0, 3).forEach(cpu => {
                popularCpusContainer.appendChild(createCpuCard(cpu));
            });
        }
    }
}

/**
 * Create a benchmark chart for the CPU
 * @param {Object} cpuData - The CPU data object
 */
function createBenchmarkChart(cpuData) {
    const chartContainer = document.getElementById('cpu-benchmark-chart');
    if (!chartContainer) return;
    
    // In a real implementation, this would use a charting library like Chart.js
    // For now, we'll create a simple visual representation
    const maxScore = 10000; // Maximum possible score for scaling
    
    chartContainer.innerHTML = `
        <div class="simple-chart">
            <div class="chart-bar">
                <div class="chart-label">Single Thread</div>
                <div class="chart-bar-outer">
                    <div class="chart-bar-inner" style="width: ${(cpuData.benchmarks.singleThread / maxScore) * 100}%">
                        ${cpuData.benchmarks.singleThread}
                    </div>
                </div>
            </div>
            <div class="chart-bar">
                <div class="chart-label">Multi Thread</div>
                <div class="chart-bar-outer">
                    <div class="chart-bar-inner" style="width: ${(cpuData.benchmarks.multiThread / maxScore) * 100}%">
                        ${cpuData.benchmarks.multiThread}
                    </div>
                </div>
            </div>
        </div>
    `;
}

/**
 * Load the list of available CPUs
 */
function loadCpuList() {
    const cpuGrid = document.querySelector('.cpu-grid');
    if (!cpuGrid) return;
    
    // Clear existing content
    cpuGrid.innerHTML = '';
    
    // Add CPU cards to the grid
    cpuDatabase.forEach(cpu => {
        cpuGrid.appendChild(createCpuCard(cpu));
    });
}

/**
 * Create a CPU card element
 * @param {Object} cpu - The CPU data object
 * @returns {HTMLElement} - The CPU card element
 */
function createCpuCard(cpu) {
    const card = document.createElement('div');
    card.className = 'cpu-card';
    card.dataset.cpu = cpu.id;
    
    card.innerHTML = `
        <div class="cpu-card-image">
            <img src="/assets/images/cpu-circuit-bg.svg" alt="${cpu.model}">
        </div>
        <div class="cpu-card-info">
            <h4>${cpu.model}</h4>
            <p>${cpu.cores} cores, ${cpu.threads} threads</p>
            <a href="/cpu?model=${encodeURIComponent(cpu.id)}" class="cpu-details-link">View Details</a>
        </div>
    `;
    
    return card;
}

/**
 * Load CPU options for comparison
 * @param {string} currentCpuId - The ID of the current CPU
 */
function loadComparisonOptions(currentCpuId) {
    const selectElement = document.getElementById('compare-cpu');
    if (!selectElement) return;
    
    // Clear existing options except the first one
    while (selectElement.options.length > 1) {
        selectElement.remove(1);
    }
    
    // Filter out the current CPU from options
    const filteredOptions = cpuDatabase.filter(cpu => cpu.id !== currentCpuId);
    
    // Add options to select element
    filteredOptions.forEach(cpu => {
        const option = document.createElement('option');
        option.value = cpu.id;
        option.textContent = cpu.model;
        selectElement.appendChild(option);
    });
}

/**
 * Compare two CPUs and display the results
 * @param {string} cpuModel1 - The first CPU model ID
 * @param {string} cpuModel2 - The second CPU model ID
 */
function compareCpus(cpuModel1, cpuModel2) {
    // Find the CPU data for both models
    const cpu1 = cpuDatabase.find(cpu => cpu.id === cpuModel1 || cpu.model === cpuModel1);
    const cpu2 = cpuDatabase.find(cpu => cpu.id === cpuModel2);
    
    if (!cpu1 || !cpu2) {
        console.error('One or both CPUs not found');
        return;
    }
    
    const comparisonResults = document.getElementById('comparison-results');
    if (!comparisonResults) return;
    
    // Create comparison table
    comparisonResults.innerHTML = `
        <table class="comparison-table">
            <thead>
                <tr>
                    <th>Specification</th>
                    <th>${cpu1.model}</th>
                    <th>${cpu2.model}</th>
                    <th>Difference</th>
                </tr>
            </thead>
            <tbody>
                <tr>
                    <td>Architecture</td>
                    <td>${cpu1.architecture}</td>
                    <td>${cpu2.architecture}</td>
                    <td>${cpu1.architecture === cpu2.architecture ? 'Same' : 'Different'}</td>
                </tr>
                <tr>
                    <td>Cores</td>
                    <td>${cpu1.cores}</td>
                    <td>${cpu2.cores}</td>
                    <td>${compareValues(cpu1.cores, cpu2.cores)}</td>
                </tr>
                <tr>
                    <td>Threads</td>
                    <td>${cpu1.threads}</td>
                    <td>${cpu2.threads}</td>
                    <td>${compareValues(cpu1.threads, cpu2.threads)}</td>
                </tr>
                <tr>
                    <td>Base Clock</td>
                    <td>${cpu1.baseClockGHz} GHz</td>
                    <td>${cpu2.baseClockGHz} GHz</td>
                    <td>${compareValues(cpu1.baseClockGHz, cpu2.baseClockGHz, 'GHz')}</td>
                </tr>
                <tr>
                    <td>Boost Clock</td>
                    <td>${cpu1.boostClockGHz} GHz</td>
                    <td>${cpu2.boostClockGHz} GHz</td>
                    <td>${compareValues(cpu1.boostClockGHz, cpu2.boostClockGHz, 'GHz')}</td>
                </tr>
                <tr>
                    <td>TDP</td>
                    <td>${cpu1.tdpWatts} W</td>
                    <td>${cpu2.tdpWatts} W</td>
                    <td>${compareValues(cpu1.tdpWatts, cpu2.tdpWatts, 'W')}</td>
                </tr>
                <tr>
                    <td>Single Thread Score</td>
                    <td>${cpu1.benchmarks.singleThread}</td>
                    <td>${cpu2.benchmarks.singleThread}</td>
                    <td>${compareValues(cpu1.benchmarks.singleThread, cpu2.benchmarks.singleThread, '', true)}</td>
                </tr>
                <tr>
                    <td>Multi Thread Score</td>
                    <td>${cpu1.benchmarks.multiThread}</td>
                    <td>${cpu2.benchmarks.multiThread}</td>
                    <td>${compareValues(cpu1.benchmarks.multiThread, cpu2.benchmarks.multiThread, '', true)}</td>
                </tr>
            </tbody>
        </table>
    `;
}

/**
 * Compare two values and return a formatted string with the difference
 * @param {number} val1 - The first value
 * @param {number} val2 - The second value
 * @param {string} unit - The unit of measurement (optional)
 * @param {boolean} higherIsBetter - Whether higher values are better (optional)
 * @returns {string} - Formatted comparison string
 */
function compareValues(val1, val2, unit = '', higherIsBetter = true) {
    const diff = val1 - val2;
    const absDiff = Math.abs(diff);
    const percentDiff = ((absDiff / val2) * 100).toFixed(1);
    
    let result = '';
    let className = '';
    
    if (diff === 0) {
        result = 'Same';
    } else if (diff > 0) {
        result = `+${absDiff}${unit} (+${percentDiff}%)`;
        className = higherIsBetter ? 'better' : 'worse';
    } else {
        result = `-${absDiff}${unit} (-${percentDiff}%)`;
        className = higherIsBetter ? 'worse' : 'better';
    }
    
    return `<span class="${className}">${result}</span>`;
}